import { Dialog } from "@dash/dialog";
import { Button, TextField } from "@dash/form";
import { Select } from "@dash/select";
import { useToast } from "@dash/toast";
import { useEffect, useMemo, useState } from "react";

import { useClientManager } from "@megaron/react-clients";
import { Serializers } from "@megaron/serializers";
import { TicketDetailDto } from "@megaron/support-contracts";

type Props = {
  ticket: TicketDetailDto;
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
};

function uniqueEmails(emails: (string | null | undefined)[]): string[] {
  const values: string[] = [];
  for (const email of emails) {
    const normalizedEmail = (email ?? "").trim().toLowerCase();
    if (!normalizedEmail) continue;
    if (!values.includes(normalizedEmail)) values.push(normalizedEmail);
  }
  return values;
}

type ContactOption = {
  label: string;
  value: string;
  email: string;
  name: string;
};

export const SupportCreateTicketDialog: React.FC<Props> = ({ ticket, isOpen, onClose, onCreated }) => {
  const toast = useToast();
  const support = useClientManager("support");
  const crm = useClientManager("crm");

  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [reporterName, setReporterName] = useState(ticket.reporterName);
  const [reporterEmail, setReporterEmail] = useState(ticket.reporterEmail);
  const [sourceMailbox, setSourceMailbox] = useState("");

  const mailboxStatusQuery = support.imapConnectionStatus().useQuery({}, { enabled: isOpen });
  const customerQuery = crm
    .customerDocBulkQuery()
    .useQuery(
      { customerUuids: ticket.crmCustomerUuid ? [ticket.crmCustomerUuid] : [] },
      { enabled: isOpen && !!ticket.crmCustomerUuid },
    );
  const contactsQuery = crm
    .customerContactsQuery()
    .useQuery(
      { customerId: ticket.crmCustomerUuid ?? ticket.id },
      { enabled: isOpen && !!ticket.crmCustomerUuid },
    );

  const mailboxOptions = useMemo(
    () => (mailboxStatusQuery.data?.mailboxes ?? []).map((mailbox) => ({ label: mailbox.email, value: mailbox.email })),
    [mailboxStatusQuery.data?.mailboxes],
  );

  const reporterEmailOptions = useMemo(() => {
    const crmCustomerEmail = customerQuery.data?.[0]?.email ?? undefined;
    const contactEmails = (contactsQuery.data ?? []).map((contact) => contact.email);
    const emails = uniqueEmails([crmCustomerEmail, ...contactEmails, ticket.reporterEmail]);
    return emails.map((email) => ({ label: email, value: email }));
  }, [contactsQuery.data, customerQuery.data, ticket.reporterEmail]);

  const contactOptions = useMemo((): ContactOption[] => {
    const options: ContactOption[] = [];
    const currentEmail = ticket.reporterEmail.trim().toLowerCase();
    const currentName = ticket.reporterName.trim() || currentEmail.split("@")[0];
    if (currentEmail) {
      options.push({ label: `${currentName} (${currentEmail})`, value: `${currentEmail}|${currentName}`, email: currentEmail, name: currentName });
    }
    const customer = customerQuery.data?.[0];
    const customerEmail = customer?.email?.trim().toLowerCase() ?? "";
    const customerName = customer?.name?.trim() ?? "";
    if (customerEmail && customerName) {
      options.push({
        label: `${customerName} (${customerEmail})`,
        value: `${customerEmail}|${customerName}`,
        email: customerEmail,
        name: customerName,
      });
    }
    for (const contact of contactsQuery.data ?? []) {
      const email = contact.email.trim().toLowerCase();
      const name = contact.name.trim();
      if (!email || !name) continue;
      options.push({
        label: `${name} (${email})`,
        value: `${email}|${name}`,
        email,
        name,
      });
    }
    const deduplicated: ContactOption[] = [];
    for (const option of options) {
      if (!deduplicated.some((item) => item.value === option.value)) deduplicated.push(option);
    }
    return deduplicated;
  }, [contactsQuery.data, customerQuery.data, ticket.reporterEmail, ticket.reporterName]);

  const [selectedContact, setSelectedContact] = useState<string>("");

  useEffect(() => {
    if (!isOpen) {
      setSubject("");
      setMessage("");
      setReporterName(ticket.reporterName);
      setReporterEmail(ticket.reporterEmail);
      setSourceMailbox("");
      setSelectedContact("");
      return;
    }
    if (!sourceMailbox && mailboxOptions.length > 0) {
      setSourceMailbox(mailboxOptions[0].value);
    }
  }, [isOpen, mailboxOptions, sourceMailbox, ticket.reporterEmail, ticket.reporterName]);

  useEffect(() => {
    if (!isOpen) return;
    if (reporterEmailOptions.length === 0) return;
    const hasActive = reporterEmailOptions.some((option) => option.value === reporterEmail);
    if (!hasActive) setReporterEmail(reporterEmailOptions[0].value);
  }, [isOpen, reporterEmail, reporterEmailOptions]);

  useEffect(() => {
    if (!isOpen || contactOptions.length === 0) return;
    const currentValue = `${reporterEmail.trim().toLowerCase()}|${reporterName.trim()}`;
    const matching = contactOptions.find((option) => option.value === currentValue);
    if (matching) {
      setSelectedContact(matching.value);
      return;
    }
    const byEmail = contactOptions.find((option) => option.email === reporterEmail.trim().toLowerCase());
    if (byEmail) {
      setSelectedContact(byEmail.value);
      setReporterName(byEmail.name);
      return;
    }
    const firstOption = contactOptions[0];
    setSelectedContact(firstOption.value);
    setReporterEmail(firstOption.email);
    setReporterName(firstOption.name);
  }, [contactOptions, isOpen, reporterEmail, reporterName]);

  const createTicketMutation = support.createTicket().useMutation({
    onSuccess: () => {
      onCreated();
      toast.info("Utworzono zgłoszenie");
      onClose();
    },
    onError: () => {
      toast.error("Nie udało się utworzyć zgłoszenia");
    },
  });
  const isBusy = "isLoading" in createTicketMutation ? createTicketMutation.isLoading : false;

  const onCreate = () => {
    const trimmedSubject = subject.trim();
    const trimmedReporterEmail = reporterEmail.trim();
    const trimmedReporterName = reporterName.trim();
    const trimmedSourceMailbox = sourceMailbox.trim();
    const trimmedMessage = message.trim();
    if (!trimmedSubject || !trimmedReporterEmail || !trimmedSourceMailbox || !trimmedMessage) {
      toast.error("Uzupełnij temat, wiadomość, kontakt i skrzynkę");
      return;
    }
    const reporterEmailResult = Serializers.email.deserialize(trimmedReporterEmail);
    if (reporterEmailResult.isFailure) {
      toast.error("Podaj poprawny email kontaktu");
      return;
    }
    createTicketMutation.mutate({
      subject: trimmedSubject,
      message: trimmedMessage,
      reporterEmail: reporterEmailResult.value,
      reporterName: trimmedReporterName || trimmedReporterEmail.split("@")[0],
      sourceMailbox: trimmedSourceMailbox,
      status: "open",
      priority: null,
      tags: null,
      assigneeEmails: null,
    });
  };

  if (!isOpen) return null;

  return (
    <Dialog onClose={isBusy ? undefined : onClose} header="Nowe zgłoszenie" placement="center" css={{ width: "420px" }}>
      <div css={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
        <TextField
          label="Temat"
          value={subject}
          onChange={setSubject}
          css={{ width: "100%", input: { height: "46px" }, padding: 0 }}
          autoFocus
        />
        <Select
          label="Skrzynka odpowiedzi"
          value={sourceMailbox}
          onChange={(value) => setSourceMailbox(value ?? "")}
          options={mailboxOptions}
          isDisabled={mailboxStatusQuery.isLoading}
        />
        <Select
          label="Email kontaktu"
          value={reporterEmail}
          onChange={(value) => {
            const nextEmail = value ?? "";
            setReporterEmail(nextEmail);
            const byEmail = contactOptions.find((option) => option.email === nextEmail);
            if (byEmail) {
              setSelectedContact(byEmail.value);
              setReporterName(byEmail.name);
            }
          }}
          options={reporterEmailOptions}
          isDisabled={reporterEmailOptions.length === 0}
        />
        <Select
          label="Kontakt"
          value={selectedContact}
          onChange={(value) => {
            const selected = contactOptions.find((option) => option.value === value);
            if (!selected) return;
            setSelectedContact(selected.value);
            setReporterEmail(selected.email);
            setReporterName(selected.name);
          }}
          options={contactOptions.map((option) => ({ label: option.label, value: option.value }))}
          isDisabled={contactOptions.length === 0}
        />
        <TextField
          label="Wiadomość"
          value={message}
          onChange={setMessage}
          multiline
          css={{ width: "100%", textarea: { minHeight: "110px", resize: "vertical" }, padding: 0 }}
        />
      </div>
      <div css={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}>
        <Button css={{ width: "auto", padding: "0.5rem 1rem" }} onClick={onCreate} isLoading={!!isBusy}>
          Utwórz
        </Button>
      </div>
    </Dialog>
  );
};
